/**
 * KHIYARSA (KMD): the public AI diagram agent template — constants and
 * loader for the "AI Diagram Template" dialog.
 *
 * Single source of truth: the committed canonical file
 * `proposals/khiyarsa-markdown/KHIYARSA-DIAGRAM-AGENT-TEMPLATE.md`, which
 * nginx aliases at TEMPLATE_PUBLIC_PATH byte-for-byte (excalidraw-static.conf).
 * The UI never bundles a copy — View/Copy/Download all read that one URL, so
 * they can never drift from the committed template.
 */

export const TEMPLATE_FILENAME = "KHIYARSA-DIAGRAM-AGENT-TEMPLATE.md";
export const TEMPLATE_PUBLIC_PATH = `/kmd/${TEMPLATE_FILENAME}`;

/** metadata shown in the dialog — kept as data so tests can assert it */
export const TEMPLATE_META = {
  specVersion: "KMD v1",
  families: [
    "Flow",
    "Architecture",
    "Sequence",
    "ER",
    "Swimlane",
    "Timeline",
    "Mindmap",
    "Tree",
  ],
  compatibleWith: ["GPT", "Claude", "Fable", "Codex", "Kimi"],
  output: ".md",
} as const;

/**
 * Example requests the user can copy and paste under the template's
 * USER REQUEST placeholder. One per UI language.
 */
export const EXAMPLE_REQUEST_EN = `Create a complete architecture diagram for a Saudi shipping platform containing merchants, the Eeasy API, an order service, a pricing engine, PostgreSQL, Redis, SMSA, Aramex, and Salla integration.`;

export const EXAMPLE_REQUEST_AR = `أنشئ مخطط سير عمل لعملية إرجاع منتج في متجر إلكتروني: العميل يقدم طلب إرجاع خلال 7 أيام، خدمة العملاء تراجع الطلب، إذا وُوفق عليه تُرسل شركة الشحن لاستلام المنتج ويُفحص في المستودع، ثم يُسترد المبلغ أو يُرفض الإرجاع حسب حالة المنتج.`;

let cached: string | null = null;

/**
 * Fetch the canonical template text from the public URL (cached for the
 * session). Throws on a non-OK response — callers show a fetch error.
 */
export const fetchTemplate = async (): Promise<string> => {
  if (cached !== null) {
    return cached;
  }
  const response = await fetch(TEMPLATE_PUBLIC_PATH, {
    headers: { Accept: "text/markdown, text/plain, */*" },
  });
  if (!response.ok) {
    throw new Error(`template fetch failed: HTTP ${response.status}`);
  }
  cached = await response.text();
  return cached;
};

/** test hook — reset the module cache */
export const resetTemplateCache = () => {
  cached = null;
};
